import { normalizeOrganizationId } from "@/shared/organizations/organization-id";
import {
  MEMBERS_PAGE_SIZE,
  ORGANIZATIONS_PAGE_SIZE,
  ORGANIZATIONS_SEARCH_MAX_LENGTH,
} from "./constants";
import type { MembersListParams, OrganizationsListParams } from "./types";

const ADMIN_ORGANIZATIONS_PATH = "/api/admin/organizations";

function clampPage(page: number): number {
  return Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1;
}

/** The search as the API receives it: trimmed and capped at its limit. */
export function normalizeSearchTerm(search: string): string {
  return search.trim().slice(0, ORGANIZATIONS_SEARCH_MAX_LENGTH).trim();
}

/** Path of `GET /api/admin/organizations/:id` (the id is one path segment). */
export function organizationDetailPath(organizationId: string): string {
  const id = normalizeOrganizationId(organizationId);
  if (id === null) throw new Error("Invalid organization id");
  return `${ADMIN_ORGANIZATIONS_PATH}/${id}`;
}

/** Path of `GET /api/admin/organizations/:id/members`. */
export function organizationMembersPath(organizationId: string): string {
  return `${organizationDetailPath(organizationId)}/members`;
}

/**
 * Query string for `GET /api/admin/organizations`. The search matches the
 * organization or the owner's display name (the API never matches an email),
 * is trimmed, and capped at the API's limit so a long paste cannot 400.
 */
export function buildOrganizationsQuery(params: OrganizationsListParams): string {
  const search = new URLSearchParams();
  search.set("page", String(clampPage(params.page)));
  search.set("limit", String(ORGANIZATIONS_PAGE_SIZE));
  const term = normalizeSearchTerm(params.search);
  if (term) search.set("search", term);
  if (params.kind) search.set("kind", params.kind);
  return `?${search.toString()}`;
}

/** Query string for `GET /api/admin/organizations/:id/members`. */
export function buildMembersQuery(params: MembersListParams): string {
  const search = new URLSearchParams();
  search.set("page", String(clampPage(params.page)));
  search.set("limit", String(MEMBERS_PAGE_SIZE));
  if (params.status) search.set("status", params.status);
  if (params.role) search.set("role", params.role);
  return `?${search.toString()}`;
}
